// ─────────────────────────────────────────────────────────────────────────────
// A RESTORED CHECKBOX IS BOUND TO THE TEXT THAT WAS TICKED.
//
// 🚨 WHY THIS EXISTS. Every answer on the application survives a reload — the
// autosave gathers formData, the declarations bag, the LOX flags, the
// co-borrower fields and the ZIP tags. The CONSENT CHECKBOXES were the one
// thing it never carried. So a borrower who filled the form, left and came back
// found the page restored and the boxes empty, and "Submit Application" refused
// with "Please agree to all items above before submitting." Loc Tran (user 354)
// reached step 3 of 3 with a complete application and emailed support saying he
// had applied. He had, by every measure except the two boxes.
//
// ⚠️ SO WHY NOT JUST STORE `true`? Because constants/applicationConsents.js
// already settles it, in its own words: the resale cap is interpolated rather
// than typed so that "a rate change alters the consent text, which changes its
// hash … A BORROWER WHO AGREED TO THE OLD RATE HAS NOT AGREED TO THE NEW ONE."
// A bare `true` restored against changed wording would carry a tick for text
// the borrower never saw — which is worse than an empty box, because the empty
// box is honest.
//
// 🔑 SO THE TICK IS STORED AS THE SHA-256 OF WHAT WAS AGREED TO, and restored
// only when the text on the page now hashes to the same thing. Change a word
// and the box comes back empty, which is the correct outcome.
//
// ⚠️ AND IT FAILS TOWARD THE EMPTY BOX. If crypto.subtle is unavailable — an
// insecure context, an old WebView — the hash is null, nothing is stored and
// nothing is restored. That is exactly today's behaviour, so the worst case of
// this change is the status quo rather than a wrongly-ticked box.
//
// 🚨 THIS IS NOT A CONSENT RECORD. The legal record is written at submit, by
// the server, with the text, its hash, the timestamp, the IP and the user
// agent. This only decides whether a checkbox starts out ticked; the borrower
// still presses Submit, and that press is what consents.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SHA-256 of a consent's text, hex-encoded.
 * @param {string} text
 * @returns {Promise<string|null>} null when the platform cannot hash
 */
export async function hashConsentText(text) {
  // ⚠️ `window`, not `globalThis` — the project's eslint env does not define
  // the latter, and this file only ever runs in a browser or a WebView.
  const subtle = (typeof window !== 'undefined'
    && window.crypto && window.crypto.subtle) || null;
  if (!subtle || typeof TextEncoder === 'undefined') return null;
  if (typeof text !== 'string' || !text.length) return null;
  try {
    const bytes = new TextEncoder().encode(text);
    const digest = await subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // ⚠️ Swallowed on purpose: an unhashable consent is an unrestored one, and
    // that is a safe outcome. Throwing here would break an autosave.
    return null;
  }
}

/**
 * 🔑 THE IDENTITY OF THE ACCOUNT-SCOPED BOX is the three documents AT THEIR
 * VERSIONS, not a body of text — it covers Terms, Privacy and E-Sign Consent,
 * each served with its own version. A new version of any one of them is a
 * different consent, so the tick must not survive it.
 *
 * @param {Array<{key:string, version:string}>} documents
 * @returns {string|null}
 */
export function accountConsentIdentity(documents) {
  if (!Array.isArray(documents) || !documents.length) return null;
  const parts = documents
    .filter((d) => d && d.key)
    .map((d) => `${d.key}@${d.version == null ? '' : d.version}`)
    .sort();
  // ⚠️ A DOCUMENT WITH NO VERSION MAKES THE IDENTITY UNUSABLE. Treating a
  // missing version as a matchable value would let a tick survive a change
  // nobody could see.
  if (parts.some((p) => p.endsWith('@'))) return null;
  return parts.join('|');
}

/**
 * Which stored ticks still match what is on the page.
 *
 * @param {Record<string,string>} stored   id → hash recorded when ticked
 * @param {Record<string,string>} current  id → hash of the text shown now
 * @returns {Record<string,boolean>}       only the ids that matched, as true
 */
export function restorableTicks(stored, current) {
  const out = {};
  if (!stored || !current) return out;
  for (const [id, hash] of Object.entries(stored)) {
    // 🔑 BOTH MUST BE PRESENT AND EQUAL. A missing current hash means the text
    // could not be hashed now; a missing stored hash means it could not be
    // hashed then. Neither is a match.
    if (hash && current[id] && hash === current[id]) out[id] = true;
  }
  return out;
}
